/**
 * Saleor Auth SDK only adds `Authorization` when `getURL(request) === jwt.iss` (string equality).
 * JWT `iss` is set by the Saleor backend (often from `PUBLIC_URL` / site settings), while the
 * browser uses `NEXT_PUBLIC_SALEOR_API_URL` — common local mismatches:
 * - `localhost` vs `127.0.0.1` vs `host.docker.internal`
 * - http vs https, different ports
 *
 * When `allowPassingTokenToThirdPartyDomains` is true, the SDK still sends the bearer token if
 * `iss` does not match (see @saleor/auth-sdk `runAuthorizedRequest`). Use only when you accept
 * that risk (typically local dev or after aligning env with Saleor).
 *
 * Align `NEXT_PUBLIC_SALEOR_API_URL` with the GraphQL origin Saleor uses for JWT `iss`, or set relax explicitly.
 */
export function getSaleorAuthFetchAdditionalParams(): {
	allowPassingTokenToThirdPartyDomains?: boolean;
} {
	if (process.env.NEXT_PUBLIC_SALEOR_AUTH_RELAX_ISS === "1") {
		return { allowPassingTokenToThirdPartyDomains: true };
	}
	if (process.env.NEXT_PUBLIC_SALEOR_AUTH_RELAX_ISS === "0") {
		return {};
	}
	// Default: relax in development so typical host/URL drift does not strip auth on checkout.
	if (process.env.NODE_ENV === "development") {
		return { allowPassingTokenToThirdPartyDomains: true };
	}
	// `next start` / Docker storefront use NODE_ENV=production; browser still hits localhost while JWT
	// `iss` may follow Saleor PUBLIC_URL — same mismatch as dev, without the dev branch above.
	if (typeof window !== "undefined") {
		const h = window.location.hostname;
		if (h === "localhost" || h === "127.0.0.1" || h === "::1") {
			return { allowPassingTokenToThirdPartyDomains: true };
		}
	}
	return {};
}
